/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import { Inject, Injectable } from '@angular/core';
import {
  OrientationDialogComponent,
} from 'src/ckeditor/component/dialog/orientation-dialog/orientation-dialog.component';
import { AeroDialogService } from 'src/aero/aero-dialog/aero-dialog.service';
import { DeleteBlockComponent } from '../../component/dialog/delete-block-dialog/delete-block-dialog.component';
import {
  ImageCarouselCaptionDialogComponent,
} from '../../component/dialog/image-carousel-caption-dialog/image-carousel-caption-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class EditorDialogService {
  /**
   * Width of dialog for mobile devices.
   */
  private readonly mobileWidth = '97%';

  /**
   * Width of dialog for non-mobile devices.
   */
  private readonly laptopWidth = '352px';

  /**
   * Bound addURL handler reference for removing the event listener.
   * @private
   */
  private boundAddURL;

  /**
   * Default Constructor.
   *
   * @param dialogService Our AeroDialogService service.
   * @param BulbBrowserService Our AngularJS BulbBrowserService.
   */
  constructor(
    private dialogService: AeroDialogService,
    @Inject('BulbBrowserService') private BulbBrowserService : any,
  ) {
  }

  /**
   * Method to call delete block dialog.
   *
   * @param editor CKEditor instance.
   */
  showDeleteBlock(type, model, imageCarouselSlide) {
    const isMobile = this.BulbBrowserService.isIOSMobile() || this.BulbBrowserService.isAndroid();
    const dialogOptions = {
      data: { type, model, imageCarouselSlide },
      width: (!isMobile) ? this.laptopWidth : this.mobileWidth,
      closeOnEscape: true,
      closable: true,
      showHeader: false,
      styleClass: 'aero-dialog delete-block-dialog',
      dismissableMask: true,
    };
    this.dialogService.show(DeleteBlockComponent, dialogOptions);
  }

  /**
   * Method to show add caption dialog.
   *
   * @param captionText Text of image carousel.
   * @param bulbBlockId Id of carousel block.
   * @param dialogConfirmBtnText Dialog button name.
   */
  showCaptionDialog(
    captionText,
    bulbBlockId,
    dialogConfirmBtnText,
  ) {
    const isMobile = this.BulbBrowserService.isIOSMobile() || this.BulbBrowserService.isAndroid();
    /**
     * Dialog options for inserting caption in image carousel.
     */
    const showCaptionOptions = {
      width: (!isMobile) ? '32em' : this.mobileWidth,
      modal: true,
      dismissableMask: true,
      styleClass: 'aero-dialog show-image-carousel-caption',
      showHeader: false,
      closeOnEscape: true,
      closable: true,
      data: {
        captionText,
        bulbBlockId,
        dialogConfirmBtnText,
      },
    };
    return this.dialogService.show(ImageCarouselCaptionDialogComponent, showCaptionOptions);
  }

  /**
   * Alert message showing we currently doesn't support copy/cut within editor.
   *
   * @param event Event on editor.
   * @param data Data on event occur.
   */
  showCopyAlert(translate, event, data) {
    data.preventDefault();
    event.stop();
    this.dialogService.showDialog({
      icon: 'fa fa-exclamation-triangle',
      iconClass: 'aero-alert',
      title: translate('copy_alert_title'),
      detail: translate('copy_alert_body'),
      showSecondary: false,
      primaryLabel: translate('button_ok'),
    });
  }

  /**
   * Method to show orientation dialog model.
   * @param value boolean value to show modal.
   * @param imageUrl image url of the image shown in modal.
   * @param contentBlock content block of current image.
   * @param editor editor instance.
   * @param orientation orienation value.
   * @param imageIndex current image index.
   * @param noOfAssets total number of images added at once.
   */
  showOrientationDialog(value, imageUrl, contentBlock, editor, orientation, imageIndex, noOfAssets: number) {
    const isMobile = this.BulbBrowserService.isIOSMobile() || this.BulbBrowserService.isAndroid();
    /**
     * Dialog options for inserting caption in image carousel.
     */
    const showCaptionOptions = {
      width: (!isMobile) ? '34em' : this.mobileWidth,
      modal: true,
      dismissableMask: value,
      styleClass: 'aero-dialog orientation1',
      showHeader: false,
      data: {
        orientationModal: value,
        imageUrl,
        contentBlock,
        editor,
        orientation,
        imageIndex,
        noOfAssets,
      },
    };
    return this.dialogService.show(OrientationDialogComponent, showCaptionOptions);
  }

  /**
   * Handles URL paste events in CKEditor.
   *
   * When a user pastes a URL, this method shows a confirmation dialog asking
   * whether to embed the link. If the user confirms, it dispatches a custom
   * `urlPasteEvent` event with the URL. If the user cancels, it inserts
   * the plain URL text into the editor as-is.
   *
   * @param editor - The CKEditor instance handling the paste.
   * @param event - The paste event from the editing view document.
   * @param data - The clipboard data object containing pasted content.
   */
  showURLPasteDialog(editor, event, data) {
    const plainText = data.dataTransfer.getData('text/plain')?.trim();
    const isUrl = plainText && /^https?:\/\/[^\s]+$/i.test(plainText);
    if (!isUrl) {
      return;
    }

    data.preventDefault();
    event.stop();

    this.dialogService.showDialog({
      icon: 'fa fa-exclamation-triangle',
      iconClass: 'aero-alert',
      title: editor.t('copy_alert_title'),
      detail: editor.t('paste_alert_body'),
      showSecondary: true,
      secondaryLabel: editor.t('snapshot_modal_not_now'),
      primaryLabel: editor.t('snapshot_file_deleted_modal_yes'),
      onPrimary: () => {
        const pasteEvent = new CustomEvent('urlPasteEvent', {
          detail: {
            url: plainText,
          },
        });

        window.dispatchEvent(pasteEvent);

        if (this.boundAddURL) {
          window.removeEventListener('embedURLErrorEvent', this.boundAddURL);
        }

        this.boundAddURL = this.addURL.bind(this, editor, plainText);

        window.addEventListener('embedURLErrorEvent', this.boundAddURL);
      },
      onSecondary: () => {
        this.addURL(editor, plainText);
      },
    });
  }

  /**
   * Inserts the given URL into the editor and removes the related error listener.
   * @param editor - The CKEditor instance.
   * @param url - The URL to insert.
   */
  private addURL(editor, url) {
    window.removeEventListener('embedURLErrorEvent', this.boundAddURL);

    const { model } = editor;
    model.change((writer) => {
      const insertPosition = model.document.selection.getFirstPosition();
      writer.insertText(url, insertPosition);
    });
  }
}
